import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

interface ProfileEditProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Profile {
  id: string;
  email: string | null;
  name: string | null;
}

export function ProfileEdit({ isOpen, onClose }: ProfileEditProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadProfile();
    }
  }, [isOpen]);

  const loadProfile = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      setProfile(profile);
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
      });
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Erro desconhecido");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!profile?.id) throw new Error("No profile ID");

      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          name: formData.name,
        })
        .eq("id", profile.id);

      if (updateError) throw updateError;

      // Only update email if it changed
      if (formData.email !== profile.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email,
        });

        if (emailError) throw emailError;
      }

      toast({
        title: "Perfil atualizado com sucesso",
        status: "success",
        duration: 3000,
      });

      onClose();
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Erro desconhecido");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar Perfil</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Nome</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Seu nome"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="Seu email"
                />
              </FormControl>

              <HStack spacing={3} width="100%" justify="flex-end" pt={4}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="submit" colorScheme="blue" isLoading={saving}>
                  Salvar
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
